import { TODO } from '@common-types/helpers';
import { Routes, paymentOptions as checkoutPaymentOptions } from '@common/constants';
import { analytics, formatBasketItems, formatPrice, trackPurchaseEvent } from '@features/analytics';
import { trackEmailCaptured } from '@features/checkout/helpers/emailCapture';
import * as Sentry from '@sentry/nextjs';
import { modalErrorShow } from '@store/modalError/actions';
import { parseTopLevelDotKeys } from '@utils/helpers';
import { getIntegrationErrorCode } from '@utils/integrationErrorCode';
import request from '@utils/request';
import { toCamelCase } from 'case-converter';
import Cookies from 'js-cookie';
import { equals } from 'ramda';
import { Dispatch } from 'redux';
import { FormSubmissionError } from '@store/errors';
import { isOrderPaymentConfirmed, Item, OrderStatus, PaymentProviders } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';
import {
  getAffiliateCookie,
  getCampaignParams,
  getOrderReferenceCookie,
  getOrderViewTokenCookie,
  removeBasketCookie,
  removeOrderReferenceCookie,
  removeOrderViewTokenCookie,
  setBasketCookie,
  setOrderReferenceCookie,
  setOrderViewTokenCookie,
} from 'shared-utils';
import { entitySelectors } from '../entity/selectors';
import { basketSelectors } from './selectors';
import { roomRateParseForBasket, wait } from './utils';
import { applyVouchersToBasketTotal } from './utils/applyVouchersToBasketTotal';
import { multiRoomRateParseForBasket } from './utils/roomRateAddToBasket';

const PREFIX = '[BASKET]';

function reduceArray(data) {
  return data.reduce((prev, actual) => prev || actual, null);
}

function reduceErrorArray(error) {
  return reduceArray(error.items);
}

function reduceTimeslotErrorArray(error) {
  return reduceArray(error.timeslot);
}

function hasIntegrationErrors(basket) {
  return basket.items.some((item) => item?.bookable?.status === 'failed');
}

function getBasketEmail(basket?: BasketDetails): string | null {
  return basket?.payment?.email || basket?.registrationEmail || null;
}

function parseAddBasketError(error) {
  const parsedError = reduceErrorArray(error);

  const errorToThrow = {
    _error: parsedError,
    quantity: undefined,
  };

  if (parsedError.code === 'quantity_exceeds_maximum' && parsedError.value === 'quantity') {
    errorToThrow.quantity = parsedError;
  }

  return errorToThrow;
}

export const FETCH_BASKET_DETAILS_REQUEST = `${PREFIX} FETCH_BASKET_DETAILS_REQUEST`;
export const FETCH_BASKET_DETAILS_SUCCESS = `${PREFIX} FETCH_BASKET_DETAILS_SUCCESS`;
export const FETCH_BASKET_DETAILS_FAILURE = `${PREFIX} FETCH_BASKET_DETAILS_FAILURE`;

const fetchBasketDetailsRequest = () => ({
  type: FETCH_BASKET_DETAILS_REQUEST,
});

const fetchBasketDetailsSuccess = (payload) => ({
  type: FETCH_BASKET_DETAILS_SUCCESS,
  payload,
});

const fetchBasketDetailsFailure = (error) => ({
  type: FETCH_BASKET_DETAILS_FAILURE,
  error,
});

export const fetchBasketDetails =
  (propertyId: number, basketId: string | number, language: string) => (dispatch: Dispatch) => {
    dispatch(fetchBasketDetailsRequest());

    return request.get(`${propertyId}/baskets/${basketId}/${language}`).then(
      async ({ data }) => {
        const basketDetails = data;

        const basketDetailsAfterVouchers = applyVouchersToBasketTotal(basketDetails);

        dispatch(fetchBasketDetailsSuccess(basketDetailsAfterVouchers));
      },
      (error) => {
        dispatch(fetchBasketDetailsFailure(error));
      }
    );
  };

export const CREATE_BASKET_DETAILS_REQUEST = `${PREFIX} CREATE_BASKET_DETAILS_REQUEST`;
export const CREATE_BASKET_DETAILS_SUCCESS = `${PREFIX} CREATE_BASKET_DETAILS_SUCCESS`;
export const CREATE_BASKET_DETAILS_FAILURE = `${PREFIX} CREATE_BASKET_DETAILS_FAILURE`;

const createBasketRequest = () => ({
  type: CREATE_BASKET_DETAILS_REQUEST,
});

const createBasketSuccess = (basketDetails) => ({
  type: CREATE_BASKET_DETAILS_SUCCESS,
  payload: basketDetails,
});

const createBasketFailure = (error) => ({
  type: CREATE_BASKET_DETAILS_FAILURE,
  error,
});

export const createBasket =
  (propertyId: number, language: string, data: any, preventPageReload?: boolean) =>
  (dispatch: Dispatch) => {
    dispatch(createBasketRequest());

    return request.post(`${propertyId}/baskets/${language}`, data).then(
      async ({ data }) => {
        const basketDetails = data;

        setBasketCookie(basketDetails.id);
        if (hasIntegrationErrors(basketDetails)) {
          Sentry.captureException(new Error('failed_integration_error'));
          dispatch(modalErrorShow({ code: 'failed_integration' }));
        }

        analytics.track({
          event: 'add_to_cart',
          params: {
            currency: basketDetails.total.currencyCode,
            value: formatPrice(basketDetails.total.amount),
            items: formatBasketItems([basketDetails.items.at(-1) as Item]),
          },
        });

        return dispatch(createBasketSuccess(basketDetails));
      },
      (error) => {
        const integrationErrorCode = getIntegrationErrorCode(error);
        if (integrationErrorCode) {
          dispatch(createBasketFailure(error));
          Sentry.captureException(error);
          dispatch(modalErrorShow({ code: integrationErrorCode }));
          throw error;
        }

        if (error.response.data && error.response.data.items) {
          dispatch(createBasketFailure(error));
          const parsedError = parseAddBasketError(error.response.data);
          // eslint-disable-next-line no-underscore-dangle
          dispatch(modalErrorShow(parsedError._error, preventPageReload));
          Sentry.captureException(error);
          throw new FormSubmissionError(parsedError);
        }

        if (error.response.data && error.response.data.error && error.response.data.error.length) {
          dispatch(modalErrorShow(error.response.data.error[0], preventPageReload));
          dispatch(createBasketFailure(error));
          Sentry.captureException(error);
          throw new FormSubmissionError(error.response.data);
        } else {
          Sentry.captureException(error);
          dispatch(modalErrorShow({ code: 'global' }));
        }

        dispatch(createBasketFailure(error));
        throw error;
      }
    ) as Promise<TODO>;
  };

export const UPDATE_BASKET_DETAILS_REQUEST = `${PREFIX} UPDATE_BASKET_DETAILS_REQUEST`;
export const UPDATE_BASKET_DETAILS_SUCCESS = `${PREFIX} UPDATE_BASKET_DETAILS_SUCCESS`;
export const UPDATE_BASKET_DETAILS_FAILURE = `${PREFIX} UPDATE_BASKET_DETAILS_FAILURE`;

const updateBasketRequest = () => ({
  type: UPDATE_BASKET_DETAILS_REQUEST,
});

export const updateBasketSuccess = (basketDetails) => ({
  type: UPDATE_BASKET_DETAILS_SUCCESS,
  payload: basketDetails,
});

const updateBasketFailure = (errors) => ({
  type: UPDATE_BASKET_DETAILS_FAILURE,
  errors,
});

export const updateBasket =
  (
    propertyId: number,
    basketId: number | string,
    language: string,
    data: any,
    disableSubmissionErrors = false,
    preventPageReload = false
  ) =>
  (dispatch, getState: () => { basket: { basketDetails: BasketDetails } }) => {
    const currentBasket = getState().basket.basketDetails;
    const currentBasketItems = currentBasket.items;

    dispatch(updateBasketRequest());
    return request.patch(`${propertyId}/baskets/${basketId}/${language}`, data).then(
      async ({ data }) => {
        const basketDetails = data;
        setBasketCookie(basketDetails.id);

        if (hasIntegrationErrors(basketDetails)) {
          Sentry.captureException(new Error('failed_integration_error'));
          dispatch(modalErrorShow({ code: 'failed_integration' }));
        }

        const itemAddedToBasket = basketDetails.items.length > currentBasketItems.length;

        if (itemAddedToBasket) {
          analytics.track({
            event: 'add_to_cart',
            params: {
              currency: basketDetails.total.currencyCode,
              value: formatPrice(basketDetails.total.amount),
              items: formatBasketItems([basketDetails.items.at(-1) as Item]),
            },
          });
        }

        if (getBasketEmail(basketDetails)) {
          trackEmailCaptured(
            String(basketDetails.id),
            basketDetails.registrationEmail ? 'registration' : 'billing'
          );
        }

        const basketDetailsAfterVouchers = applyVouchersToBasketTotal(basketDetails);

        return dispatch(updateBasketSuccess(basketDetailsAfterVouchers));
      },
      (error) => {
        const integrationErrorCode = getIntegrationErrorCode(error);
        if (integrationErrorCode) {
          Sentry.captureException(error);
          dispatch(updateBasketFailure(error));
          dispatch(modalErrorShow({ code: integrationErrorCode }));
          throw error;
        }

        if (error.response.status === 422) {
          Sentry.captureException(error);

          const itemError = Array.isArray(error.response.data?.items)
            ? error.response.data.items.find((item) => item?.code)
            : null;

          dispatch(updateBasketFailure(error));
          dispatch(
            modalErrorShow(error.response.data?._error ?? itemError ?? { code: 'global' }, true)
          );
        }
        if (error.response.status === 404) {
          Sentry.captureException(error);
          dispatch(updateBasketFailure(error));
          return dispatch(createBasket(propertyId, language, data));
        }

        if (error.response.data && error.response.data.items) {
          if (!disableSubmissionErrors) {
            dispatch(updateBasketFailure(error));
            const parsedError = parseAddBasketError(error.response.data);
            // eslint-disable-next-line no-underscore-dangle
            dispatch(modalErrorShow(parsedError._error, preventPageReload));
            Sentry.captureException(error);
            throw new FormSubmissionError(parsedError);
          }

          return dispatch(updateBasketFailure(reduceErrorArray(error.response.data)));
        }
        if (error.response.data && error.response.data.error && error.response.data.error.length) {
          dispatch(modalErrorShow(error.response.data.error[0], preventPageReload));
          dispatch(createBasketFailure(error));
          Sentry.captureException(error);
          throw new FormSubmissionError(error.response.data);
        } else if (error.response.data && error.response.data.timeslot) {
          dispatch(modalErrorShow(reduceTimeslotErrorArray(error.response.data)));
        } else {
          Sentry.captureException(error);
          dispatch(modalErrorShow({ code: 'global' }));
        }

        dispatch(updateBasketFailure(error));
        throw error;
      }
    ) as Promise<TODO>;
  };

export const updateBasketBillingDetails =
  (propertyId: number, basketId: string | number, payment: Record<string, unknown>) => (dispatch) =>
    request
      .patch(`${propertyId}/baskets/${basketId}/billing-details`, payment, {
        skipUnauthorisedRedirect: true,
      })
      .then(({ data }) => dispatch(updateBasketSuccess(applyVouchersToBasketTotal(data))));

export const ADD_PROMO_FAILURE = `${PREFIX} ADD_PROMO_FAILURE`;

export const applyPromoCode =
  (propertyId: number, basketId: string | number, promoCode: string | null) => async (dispatch) => {
    dispatch(updateBasketRequest());
    try {
      if (promoCode) {
        const encodedPromoCode = encodeURIComponent(promoCode.trim());
        const { data } = await request.patch(
          `${propertyId}/baskets/${basketId}/promo/${encodedPromoCode}`
        );

        const basketDetails = data;

        dispatch({
          type: UPDATE_BASKET_DETAILS_SUCCESS,
          payload: basketDetails,
        });
      } else {
        const { data } = await request.patch(`${propertyId}/baskets/${basketId}/remove-promo`);

        const basketDetails = data;

        dispatch({ type: UPDATE_BASKET_DETAILS_SUCCESS, payload: basketDetails });
      }
    } catch (errors: any) {
      dispatch({ type: ADD_PROMO_FAILURE, errors });
    }
  };

export const roomRateAddToBasket = roomRateParseForBasket(updateBasket, createBasket);
export const multiRoomRateAddToBasket = multiRoomRateParseForBasket(updateBasket, createBasket);

export const CLEAR_BASKET_ERRORS = `${PREFIX} CLEAR_BASKET_ERRORS`;
export const clearBasketErrors = () => ({
  type: CLEAR_BASKET_ERRORS,
});

export const CLEAR_PROMO_CODE_FAILURE = `${PREFIX} CLEAR_PROMO_CODE_FAILURE`;
export const clearPromoCodeFailure = () => ({
  type: CLEAR_PROMO_CODE_FAILURE,
});

export const UPDATE_ORDER = `${PREFIX} UPDATE_ORDER`;

export const updateOrder = (order) => ({
  type: UPDATE_ORDER,
  payload: order,
});

export const UPDATE_BILLING_REQUEST = `${PREFIX} UPDATE_BILLING_REQUEST`;
export const UPDATE_BILLING_SUCCESS = `${PREFIX} UPDATE_BILLING_SUCCESS`;
export const UPDATE_BILLING_FAILURE = `${PREFIX} UPDATE_BILLING_FAILURE`;

const updateBillingRequest = () => ({
  type: UPDATE_BILLING_REQUEST,
});

const updateBillingSuccess = (order) => ({
  type: UPDATE_BILLING_SUCCESS,
  payload: order,
});

const updateBillingFailure = (error) => ({
  type: UPDATE_BILLING_FAILURE,
  payload: error,
});

export const updateBilling = (billingData) => async (dispatch, getState) => {
  dispatch(updateBillingRequest());
  const state = getState();
  const { id: propertyId } = entitySelectors.getProperty(state);
  const order = basketSelectors.getOrder(state);
  let orderReference = order?.orderReference;

  if (!orderReference) {
    orderReference = getOrderReferenceCookie();
  }

  if (!orderReference) {
    const error = new Error('Missing order reference');
    dispatch(updateBillingFailure(error));
    throw error;
  }

  try {
    const { data } = await request.patch(
      `${propertyId}/orders/${orderReference}/billing`,
      billingData
    );
    dispatch(updateBillingSuccess(data));
    return data;
  } catch (error) {
    dispatch(updateBillingFailure(error));
    throw error;
  }
};

export const UPDATE_PAYMENT_OPTION_REQUEST = `${PREFIX} UPDATE_PAYMENT_OPTION_REQUEST`;
export const UPDATE_PAYMENT_OPTION_SUCCESS = `${PREFIX} UPDATE_PAYMENT_OPTION_SUCCESS`;
export const UPDATE_PAYMENT_OPTION_FAILURE = `${PREFIX} UPDATE_PAYMENT_OPTION_FAILURE`;

export const updatePaymentOptionRequest = () => ({
  type: UPDATE_PAYMENT_OPTION_REQUEST,
});

export const updatePaymentOptionSuccess = (order) => ({
  type: UPDATE_PAYMENT_OPTION_SUCCESS,
  payload: order,
});

export const updatePaymentOptionFailure = (error) => ({
  type: UPDATE_PAYMENT_OPTION_FAILURE,
  payload: error,
});

export const ORDER_STATUS_REQUEST = `${PREFIX} ORDER_STATUS_REQUEST`;
export const ORDER_STATUS_SUCCESS = `${PREFIX} ORDER_STATUS_SUCCESS`;
export const ORDER_STATUS_FAILURE = `${PREFIX} ORDER_STATUS_FAILURE`;
export const ORDER_STATUS_UNKNOWN = `${PREFIX} ORDER_STATUS_UNKNOWN`;
export const ORDER_STATUS_CARD_DECLINED = `${PREFIX} ORDER_STATUS_CARD_DECLINED`;
export const ORDER_STATUS_RESET = `${PREFIX} ORDER_STATUS_RESET`;

export const orderStatusRequest = () => ({
  type: ORDER_STATUS_REQUEST,
});

export const orderStatusSuccess = (order) => ({
  type: ORDER_STATUS_SUCCESS,
  payload: order,
});

export const orderStatusFailure = (error) => ({
  type: ORDER_STATUS_FAILURE,
  payload: error,
});

export const orderStatusUnknown = () => ({
  type: ORDER_STATUS_UNKNOWN,
});

export const orderStatusCardDeclined = (clientSecret) => ({
  type: ORDER_STATUS_CARD_DECLINED,
  payload: clientSecret,
});

export const orderStatusReset = () => ({
  type: ORDER_STATUS_RESET,
});

const pollTimeout = 60;

const awaitOrderStatus = async (
  propertyId,
  orderReference,
  paymentMethod,
  dispatch,
  pollAttempt = 1
) => {
  if (pollAttempt > pollTimeout) {
    return dispatch(orderStatusUnknown());
  }

  try {
    const response = await request.get(`${propertyId}/orders/${orderReference}/status`);
    const status = response?.data?.orderStatus;

    if (isOrderPaymentConfirmed(status)) {
      return dispatch(orderStatusSuccess(response?.data));
    }

    if (status === OrderStatus.PENDING_PAYMENT) {
      await wait(1000);
      return awaitOrderStatus(propertyId, orderReference, paymentMethod, dispatch, pollAttempt + 1);
    }

    return dispatch(orderStatusFailure(response?.data));
  } catch (error) {
    const { status, data } = error?.['response'];

    if (data?.pmId !== paymentMethod) {
      await wait(1000);
      return awaitOrderStatus(propertyId, orderReference, paymentMethod, dispatch, pollAttempt + 1);
    }

    if (![402, 422].includes(status)) {
      throw error;
    }

    if (status === 422 && data?.clientSecret) {
      return dispatch(orderStatusCardDeclined(data?.clientSecret));
    }

    await wait(1000);
    return awaitOrderStatus(propertyId, orderReference, paymentMethod, dispatch, pollAttempt + 1);
  }
};

export const CREATE_ORDER_REQUEST = `${PREFIX} CREATE_ORDER_REQUEST`;
export const CREATE_ORDER_SUCCESS = `${PREFIX} CREATE_ORDER_SUCCESS`;
export const CREATE_ORDER_FAILURE = `${PREFIX} CREATE_ORDER_FAILURE`;

export const FETCH_ORDER_REQUEST = `${PREFIX} FETCH_ORDER_REQUEST`;
export const FETCH_ORDER_SUCCESS = `${PREFIX} FETCH_ORDER_SUCCESS`;
export const FETCH_ORDER_FAILURE = `${PREFIX} FETCH_ORDER_FAILURE`;

export const createOrderRequest = () => ({
  type: CREATE_ORDER_REQUEST,
});

export const createOrderSuccess = (order) => ({
  type: CREATE_ORDER_SUCCESS,
  payload: order,
});

export const createOrderFailure = (error) => ({
  type: CREATE_ORDER_FAILURE,
  payload: error,
});

export const fetchOrderRequest = () => ({
  type: FETCH_ORDER_REQUEST,
});

export const fetchOrderSuccess = (order) => ({
  type: FETCH_ORDER_SUCCESS,
  payload: order,
});

export const fetchOrderFailure = (error) => ({
  type: FETCH_ORDER_FAILURE,
  payload: error,
});

const awaitZeroOrderStatus = async ({ dispatch, propertyId, orderReference, pollAttempt = 1 }) => {
  if (pollAttempt > pollTimeout) {
    throw new Error('Timeout exceeded polling status endpoint');
  }

  try {
    dispatch(orderStatusRequest());
    const response = await request.get(`${propertyId}/orders/${orderReference}/status`);
    const status = response?.data?.orderStatus;

    if (isOrderPaymentConfirmed(status)) {
      return dispatch(orderStatusSuccess(response?.data));
    }

    if (status === OrderStatus.PENDING_PAYMENT) {
      return awaitZeroOrderStatus({
        dispatch,
        propertyId,
        orderReference,
        pollAttempt: pollAttempt + 1,
      });
    }

    throw new Error(`Unexpected order status: ${status}`);
  } catch (error) {
    const { status } = error?.['response'] ?? {};

    if (status !== 402) {
      throw error;
    }

    return awaitZeroOrderStatus({
      dispatch,
      propertyId,
      orderReference,
      pollAttempt: pollAttempt + 1,
    });
  }
};

export const createOrder = (postData) => async (dispatch, getState) => {
  const state = getState();
  const propertyId = entitySelectors.getProperty(state)?.id;
  removeOrderReferenceCookie();
  removeOrderViewTokenCookie();
  dispatch(createOrderRequest());
  const experimentAnonymousId = Cookies.get('gbuuid');
  const affiliateCode = getAffiliateCookie();
  const campaignParams = getCampaignParams();
  try {
    const { data } = await request.post(`${propertyId}/orders/create`, {
      ...postData,
      ...(experimentAnonymousId ? { experimentAnonymousId } : {}),
      ...(affiliateCode ? { affiliateCode } : {}),
      ...campaignParams,
    });
    const { requiresPayment, orderReference } = data;
    dispatch(createOrderSuccess(data));

    if (orderReference) {
      setOrderReferenceCookie(orderReference);
    }

    if (data.viewToken) {
      setOrderViewTokenCookie(data.viewToken);
    }

    if (requiresPayment || postData?.skipCompletion) {
      return data;
    }

    await awaitZeroOrderStatus({ dispatch, propertyId, orderReference });
    return data;
  } catch (error) {
    return dispatch(createOrderFailure(error));
  }
};

export const startSinglePageCheckout = (language: string) => async (dispatch, getState) => {
  const basketDetails = getState().basket.basketDetails;
  const propertyId = entitySelectors.getProperty(getState())?.id;

  if (!propertyId || !basketDetails?.id) {
    return undefined;
  }

  const synced = await dispatch(
    updateBasket(propertyId, basketDetails.id, language, {
      promoCode: basketDetails.promoCode,
      items: basketDetails.items,
    })
  ).catch(() => undefined);

  if (synced?.type !== UPDATE_BASKET_DETAILS_SUCCESS) {
    return undefined;
  }

  const syncedBasket = synced.payload ?? basketDetails;

  return dispatch(
    createOrder({
      basket: { id: basketDetails.id },
      agreedToMarketingEmail: false,
      agreedToMarketingSms: false,
      paymentOption: syncedBasket.fullPaymentAllowed
        ? checkoutPaymentOptions.FULL
        : checkoutPaymentOptions.DEFERRED,
      skipCompletion: true,
    })
  );
};

export const fetchOrder = (orderReference, viewToken?) => async (dispatch, getState) => {
  const state = getState();
  const propertyId = entitySelectors.getProperty(state)?.id;
  dispatch(fetchOrderRequest());
  try {
    const { data } = await request.get(`${propertyId}/orders/${orderReference}/view`, {
      params: { viewToken: viewToken ?? getOrderViewTokenCookie() },
    });
    if (data.viewToken) {
      setOrderViewTokenCookie(data.viewToken);
    }
    dispatch(fetchOrderSuccess(data));
    return data;
  } catch (error) {
    dispatch(fetchOrderFailure(error));
    throw error;
  }
};

const paymentAdapter = (payment) => ({
  email: payment.email,
  phone: `${payment.phoneNumberPrefix}${payment.phoneNumber}`,
});

export const ANALYTICS_CONVERSION = `${PREFIX} ANALYTICS_CONVERSION`;

export const analyticsConversion = (order) => ({
  type: ANALYTICS_CONVERSION,
  payload: order,
});

export const makePayment =
  ({ elements, stripe, initialValues, formValues, datatransRef, paymentProvider }) =>
  async (dispatch, getState) => {
    dispatch(orderStatusRequest());
    const state = getState();
    const property = entitySelectors.getProperty(state);
    const basketDetails = basketSelectors.getBasketDetails(state);
    const order = basketSelectors.getOrder(state);

    const clientSecret = order?.clientSecret;
    let orderReference = order?.orderReference;

    if (!orderReference) {
      orderReference = getOrderReferenceCookie();
    }

    try {
      if (!equals(formValues, initialValues)) {
        const orderResponse = await request.patch(
          `${property.id}/orders/${orderReference}/update`,
          formValues
        );
        dispatch(updateOrder(orderResponse.data));
      }

      // Handle Datatrans payment flow
      if (paymentProvider === PaymentProviders.DATATRANS) {
        if (!datatransRef) {
          throw new Error('Datatrans payment form not initialized');
        }

        const expiryValues = datatransRef.getExpiryValues();

        // Submit Datatrans secure fields with expiry data
        // This triggers 3DS if required - the success event includes redirect URL
        const submitResult = await datatransRef.submit(expiryValues.month, expiryValues.year);

        // Handle 3D Secure redirect if needed
        // After 3DS, user returns to confirmation page which calls confirmPayment
        if (submitResult.redirect) {
          window.location.href = submitResult.redirect;
          return;
        }

        // No 3DS required - call backend to authorize/confirm the payment
        const authorizeResponse = await request.post(
          `${property.id}/orders/${orderReference}/authorize`,
          { provider: PaymentProviders.DATATRANS.toLowerCase() }
        );

        // Poll for order status
        const response = await awaitOrderStatus(
          property.id,
          orderReference,
          authorizeResponse.data?.paymentId,
          dispatch
        );

        dispatch(analyticsConversion(order));

        if (typeof window !== 'undefined') {
          trackPurchaseEvent(order, property, 'payment_success');
        }
        return response;
      }

      // Handle Stripe payment flow
      if (!stripe && !clientSecret) {
        throw new Error('Stripe has not loaded');
      }

      const isPaymentIntent = clientSecret?.startsWith('pi_');

      let stripeResponse;
      let paymentMethod;

      const paymentOptions = {
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${Routes.CONFIRMATION}`,
          payment_method_data: {
            billing_details: paymentAdapter(basketDetails.payment),
          },
        },
        redirect: 'if_required',
      };

      if (isPaymentIntent) {
        stripeResponse = await stripe.confirmPayment(paymentOptions);

        const { error, paymentIntent } = stripeResponse;
        if (error) {
          throw new Error(error.message);
        }
        paymentMethod = paymentIntent?.payment_method;
      } else {
        stripeResponse = await stripe.confirmSetup(paymentOptions);

        const { error, setupIntent } = stripeResponse;
        if (error) {
          throw new Error(error.message);
        }
        paymentMethod = setupIntent?.payment_method;
      }

      const response = await awaitOrderStatus(property.id, orderReference, paymentMethod, dispatch);

      dispatch(analyticsConversion(order));

      if (typeof window !== 'undefined') {
        trackPurchaseEvent(order, property, 'payment_success');
      }
      return response;
    } catch (error) {
      return dispatch(orderStatusFailure(error));
    }
  };

export const updateAcknowledgement =
  ({ initialValues, formValues }) =>
  async (dispatch, getState) => {
    const state = getState();
    const { id: propertyId } = entitySelectors.getProperty(state);
    const order = basketSelectors.getOrder(state);
    let orderReference = order?.orderReference;

    if (!orderReference) {
      orderReference = getOrderReferenceCookie();
    }

    try {
      if (!equals(formValues, initialValues)) {
        const order = await request.patch(
          `${propertyId}/orders/${orderReference}/update`,
          formValues
        );
        dispatch(updateOrder(order.data));
      }
    } catch (error) {
      return dispatch(orderStatusFailure(error));
    }
  };

// Idempotent on the server: returns the current OrderViewModel even when the
// order has already been completed, so this is safe to call unconditionally
// from the acknowledgement flow.
export const completeOrder = () => async (dispatch, getState) => {
  const state = getState();
  const { id: propertyId } = entitySelectors.getProperty(state);
  const order = basketSelectors.getOrder(state);
  let orderReference = order?.orderReference;

  if (!orderReference) {
    orderReference = getOrderReferenceCookie();
  }

  if (!orderReference) {
    return undefined;
  }

  try {
    const { data } = await request.post(`${propertyId}/orders/${orderReference}/complete`);
    dispatch(updateOrder(data));
    return data;
  } catch (error) {
    dispatch(orderStatusFailure(error));
    throw error;
  }
};

export const updatePaymentOption =
  ({ formValues }) =>
  async (dispatch, getState) => {
    dispatch(updatePaymentOptionRequest());
    const state = getState();
    const { id: propertyId } = entitySelectors.getProperty(state);
    const order = basketSelectors.getOrder(state);
    let orderReference = order?.orderReference;

    if (!orderReference) {
      orderReference = getOrderReferenceCookie();
    }

    try {
      const updatedOrder = await request.patch(
        `${propertyId}/orders/${orderReference}/update`,
        formValues
      );
      dispatch(updatePaymentOptionSuccess(updatedOrder.data));

      return updatedOrder.data;
    } catch (error) {
      dispatch(updatePaymentOptionFailure(error));
      throw error;
    }
  };

export const CLEAR_BASKET = `${PREFIX} CLEAR_BASKET`;

export const clearBasket = () => (dispatch) => {
  removeBasketCookie();
  removeOrderReferenceCookie();
  dispatch({ type: CLEAR_BASKET });
};

export const UPDATE_BASKET_PRICING_REQUEST = `${PREFIX} UPDATE_BASKET_PRICING_REQUEST`;
export const UPDATE_BASKET_PRICING_SUCCESS = `${PREFIX} UPDATE_BASKET_PRICING_SUCCESS`;
export const UPDATE_BASKET_PRICING_FAILURE = `${PREFIX} UPDATE_BASKET_PRICING_FAILURE`;

const updateBasketPricingRequest = () => ({
  type: UPDATE_BASKET_PRICING_REQUEST,
});

const updateBasketPricingSuccess = (basketPricing) => ({
  type: UPDATE_BASKET_PRICING_SUCCESS,
  payload: basketPricing,
});

const updateBasketPricingFailure = (errors) => ({
  type: UPDATE_BASKET_PRICING_FAILURE,
  errors,
});

export const fetchBasketPricing = (propertyId, basketId, items) => (dispatch) => {
  dispatch(updateBasketPricingRequest);

  request
    .post(`${propertyId}/baskets/${basketId}/pricing`, items)
    .then(({ data }) => {
      dispatch(updateBasketPricingSuccess(data));
    })
    .catch((error) => {
      const parsedError = toCamelCase(parseTopLevelDotKeys(error.response.data));
      dispatch(updateBasketPricingFailure(parsedError));

      throw new FormSubmissionError({
        // we can lost "_error" key with toCamelCase()
        _error: parseTopLevelDotKeys(error.response.data).error,
        ...parsedError,
      });
    });
};

export const DELETE_BASKET_NOTIFICATION_REQUEST = `${PREFIX} DELETE_BASKET_NOTIFICATION_REQUEST`;
export const DELETE_BASKET_NOTIFICATION_SUCCESS = `${PREFIX} DELETE_BASKET_NOTIFICATION_SUCCESS`;
export const DELETE_BASKET_NOTIFICATION_FAILURE = `${PREFIX} DELETE_BASKET_NOTIFICATION_FAILURE`;

const deleteBasketNotificationRequest = () => ({
  type: DELETE_BASKET_NOTIFICATION_REQUEST,
});

const deleteBasketNotificationSuccess = (payload) => ({
  type: DELETE_BASKET_NOTIFICATION_SUCCESS,
  payload,
});

const deleteBasketNotificationError = (error) => ({
  type: DELETE_BASKET_NOTIFICATION_FAILURE,
  payload: error,
});

export const deleteBasketNotification = (propertyId, basketId, notificationId) => (dispatch) => {
  dispatch(deleteBasketNotificationRequest());

  return request
    .delete(`${propertyId}/baskets/${basketId}/notifications/${notificationId}`)
    .then(({ data }) => {
      dispatch(deleteBasketNotificationSuccess(data));
    })
    .catch((error) => {
      dispatch(deleteBasketNotificationError(error));
    });
};
